import React, { useState, useEffect } from 'react';
import { Card, Button, message, Tabs, Table, Tag, Space, Badge, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import { essayAPI } from '../services/api';
import { Essay } from '../types';
import EssayListTable from '../components/EssayListTable';
import { FileTextOutlined, TeamOutlined, CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { getTopicFromEssay } from '../utils/topicHelper';
import {
  buildFilters,
  compareDate,
  compareNumber,
  compareText,
  createValueFilter,
} from '../utils/tableHelpers';
import './HistoryPage.css';


const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [essays, setEssays] = useState<Essay[]>([]);
  const [batchGroups, setBatchGroups] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('individual');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filterValues, setFilterValues] = useState<Record<string, string[] | null>>({});
  const [sortState, setSortState] = useState<{
    columnKey?: string;
    order?: 'ascend' | 'descend' | null;
  }>({
    columnKey: 'created_at',
    order: 'descend',
  });

  /**
   * 把表格 filters 里的一列统一收敛为单选值数组，便于透传后端。
   */
  const normalizeFilterValue = (value: any): string[] | null => {
    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }

    return value.map((item) => String(item));
  };

  useEffect(() => {
    if (activeTab === 'individual') {
      fetchEssays();
    } else {
      fetchBatchEssays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagination.current, pagination.pageSize, filterValues, sortState]);

  /**
   * 拉取当前教师下的全部作文记录，供历史页做前端排序、筛选和分页。
   */
  const fetchAllEssays = async () => {
    const pageSize = 100;
    let page = 1;
    let total = 0;
    let hasMore = true;
    const allEssays: Essay[] = [];

    while (hasMore) {
      const response = await essayAPI.list({
        page,
        page_size: pageSize,
      });
      const currentPageData = response.data.data || [];

      total = response.data.total || currentPageData.length;
      allEssays.push(...currentPageData);
      page += 1;
      hasMore = allEssays.length < total && currentPageData.length > 0;
    }

    return allEssays;
  };

  const fetchEssays = async () => {
    setLoading(true);
    try {
      const response = await essayAPI.list({
        page: pagination.current,
        page_size: pagination.pageSize,
        title: filterValues.title?.[0],
        student_name: filterValues.student?.[0],
        class_name: filterValues.class?.[0],
        status: filterValues.status?.[0],
        score_band: filterValues.score?.[0],
        sort_by: sortState.columnKey === 'student'
          ? 'student_name'
          : sortState.columnKey === 'class'
            ? 'class_name'
            : sortState.columnKey || 'created_at',
        sort_order: sortState.order === 'ascend' ? 'asc' : 'desc',
      });

      const nextEssays = response.data.data || [];
      const total = response.data.total || 0;
      const maxPage = Math.max(1, Math.ceil(total / pagination.pageSize));

      if (pagination.current > maxPage) {
        setPagination((prevPagination) => ({
          ...prevPagination,
          current: maxPage,
          total,
        }));
        return;
      }

      setEssays(nextEssays);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total,
      }));
    } catch (error) {
      console.error('Failed to fetch essays:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取批次作文
  const fetchBatchEssays = async () => {
    setLoading(true);
    try {
      const allEssays = await fetchAllEssays();

      // 按批次ID分组
      const groups: { [key: string]: any } = {};
      allEssays.forEach((essay: any) => {
        if (essay.batch_id) {
          if (!groups[essay.batch_id]) {
            const topic = getTopicFromEssay(essay);
            groups[essay.batch_id] = {
              batchId: essay.batch_id,
              title: topic?.title || '未设置',
              requirements: topic?.requirements || '',
              essays: [],
              createdAt: essay.created_at,
              totalCount: 0,
              completedCount: 0,
            };
          }
          groups[essay.batch_id].essays.push(essay);
          groups[essay.batch_id].totalCount++;
          if (essay.status === 'completed') {
            groups[essay.batch_id].completedCount++;
          }
        }
      });

      // 转换为数组并按时间排序
      const batchList = Object.values(groups).sort((a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setBatchGroups(batchList);
    } catch (error) {
      console.error('Failed to fetch batch essays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await essayAPI.delete(id);
      message.success('删除成功');
      fetchEssays();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 继续批改
  const handleContinueBatch = (batchId: string) => {
    navigate(`/batch-correction/${batchId}`);
  };

  const renderBatchMobileList = () => {
    if (!loading && batchGroups.length === 0) {
      return (
        <div className="batch-mobile-empty">
          <Empty description="暂无批次记录" />
        </div>
      );
    }

    return (
      <div className="batch-mobile-list">
        {batchGroups.map((record) => {
          const percentage = record.totalCount > 0
            ? Math.round((record.completedCount / record.totalCount) * 100)
            : 0;

          return (
            <Card
              key={record.batchId}
              className="batch-mobile-card"
              loading={loading}
              size="small"
              title={
                <Space>
                  <TeamOutlined />
                  <span>{record.title}</span>
                </Space>
              }
            >
              <div className="batch-mobile-meta">
                <div>
                  <span className="batch-mobile-label">学生数量</span>
                  <strong>{record.totalCount}</strong>
                </div>
                <div>
                  <span className="batch-mobile-label">完成进度</span>
                  <strong>{record.completedCount}/{record.totalCount}</strong>
                </div>
                <div>
                  <span className="batch-mobile-label">完成率</span>
                  <strong>{percentage}%</strong>
                </div>
                <div>
                  <span className="batch-mobile-label">创建时间</span>
                  <strong>{new Date(record.createdAt).toLocaleDateString('zh-CN')}</strong>
                </div>
              </div>
              <Button
                type="primary"
                icon={<TeamOutlined />}
                onClick={() => handleContinueBatch(record.batchId)}
                block
                className="batch-mobile-action"
              >
                继续批改
              </Button>
            </Card>
          );
        })}
      </div>
    );
  };

  // 批次列表列配置
  const batchColumns = [
    {
      title: '批次标题',
      dataIndex: 'title',
      key: 'title',
      sorter: compareText((record: any) => record.title),
      filters: buildFilters(batchGroups, (record: any) => record.title, {
        includeEmpty: true,
        emptyText: '未设置标题',
      }),
      onFilter: createValueFilter((record: any) => record.title),
      filterSearch: true,
      render: (text: string) => (
        <Space>
          <FileTextOutlined />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: '学生数量',
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: 120,
      sorter: compareNumber((record: any) => record.totalCount),
      render: (count: number) => (
        <Badge count={count} style={{ backgroundColor: '#1890ff' }} />
      ),
    },
    {
      title: '完成进度',
      key: 'progress',
      width: 150,
      sorter: compareNumber((record: any) => (
        record.totalCount > 0 ? record.completedCount / record.totalCount : 0
      )),
      filters: [
        { text: '已完成', value: 'completed' },
        { text: '进行中', value: 'processing' },
      ],
      onFilter: (value: boolean | string | number | bigint, record: any) => (
        value === 'completed'
          ? record.completedCount === record.totalCount
          : record.completedCount < record.totalCount
      ),
      render: (_: any, record: any) => {
        const percentage = record.totalCount > 0
          ? Math.round((record.completedCount / record.totalCount) * 100)
          : 0;
        return (
          <Space>
            {record.completedCount === record.totalCount ? (
              <Tag icon={<CheckCircleOutlined />} color="success">
                已完成
              </Tag>
            ) : (
              <Tag icon={<SyncOutlined spin />} color="processing">
                {record.completedCount}/{record.totalCount}
              </Tag>
            )}
            <span style={{ color: '#999', fontSize: 12 }}>{percentage}%</span>
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: compareDate((record: any) => record.createdAt),
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: any) => (
        <Button
          type="primary"
          size="small"
          icon={<TeamOutlined />}
          onClick={() => handleContinueBatch(record.batchId)}
        >
          继续批改
        </Button>
      ),
    },
  ];


  return (
    <div className="history-page">
      <Card
        className="history-main-card"
        title="作文批改记录"
        extra={
          <Space className="history-page-actions">
            <Button onClick={() => navigate('/batch-correction')}>
              <TeamOutlined /> 批量批改
            </Button>
            <Button type="primary" onClick={() => navigate('/essay/new')}>
              <FileTextOutlined /> 单篇批改
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'individual',
              label: (
                <span>
                  <FileTextOutlined /> 单篇记录
                </span>
              ),
              children: (
                <EssayListTable
                  essays={essays}
                  loading={loading}
                  remote
                  filterValues={filterValues}
                  sortState={sortState}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    responsive: true,
                    showTotal: (total: number) => `共 ${total} 篇作文`,
                  }}
                  onChange={(paginationInfo: any, nextFilters?: Record<string, any>, sorter?: any) => {
                    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;

                    setFilterValues({
                      title: normalizeFilterValue(nextFilters?.title),
                      student: normalizeFilterValue(nextFilters?.student),
                      class: normalizeFilterValue(nextFilters?.class),
                      status: normalizeFilterValue(nextFilters?.status),
                      score: normalizeFilterValue(nextFilters?.score),
                    });
                    setSortState({
                      columnKey: typeof normalizedSorter?.columnKey === 'string'
                        ? normalizedSorter.columnKey
                        : 'created_at',
                      order: normalizedSorter?.order || 'descend',
                    });
                    setPagination((prevPagination) => ({
                      ...prevPagination,
                      current: paginationInfo.current,
                      pageSize: paginationInfo.pageSize || 10,
                    }));
                  }}
                  onDelete={handleDelete}
                  showActions={true}
                />
              ),
            },
            {
              key: 'batch',
              label: (
                <span>
                  <TeamOutlined /> 批次管理
                </span>
              ),
              children: (
                <>
                  <div className="batch-table-desktop">
                    <Table
                      columns={batchColumns}
                      dataSource={batchGroups}
                      rowKey="batchId"
                      loading={loading}
                      pagination={false}
                      locale={{ emptyText: '暂无批次记录' }}
                    />
                  </div>
                  <div className="batch-table-mobile">
                    {renderBatchMobileList()}
                  </div>
                </>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default HistoryPage;
